/**
 * AdminPage.jsx
 * 관리자 사용자 관리 페이지 - 기존 admin.html 1:1 이관
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../services/api';
import Header from '../components/layout/Header';

export default function AdminPage() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [adminUsername, setAdminUsername] = useState('관리자');
    const [stats, setStats] = useState({
        total: 0,
        approved: 0,
        pending: 0,
        admins: 0
    });

    // 날짜 포맷팅
    const formatDate = (isoString) => {
        if (!isoString) return '-';

        // [Fix] DB에 저장된 UTC 시간(Naive)을 로컬 시간으로 정확히 변환하기 위해 'Z' 추가
        // 이렇게 하면 브라우저가 UTC로 인식하고 사용자의 로컬 타임존(KST)으로 변환하여 표시함
        const safeIsoString = (!isoString.endsWith('Z') && !isoString.includes('+'))
            ? isoString + 'Z'
            : isoString;

        const date = new Date(safeIsoString);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // 사용자 목록 로드
    const loadUsers = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await api.getAdminUsers();

            if (data.users) {
                setUsers(data.users);

                // 통계 계산
                setStats({
                    total: data.users.length,
                    approved: data.users.filter(u => u.is_approved && !u.is_admin).length,
                    pending: data.users.filter(u => !u.is_approved).length,
                    admins: data.users.filter(u => u.is_admin).length
                });
            }
        } catch (err) {
            if (err.response?.status === 401) {
                navigate('/login');
                return;
            }
            if (err.response?.status === 403) {
                alert('관리자 권한이 필요합니다.');
                navigate('/');
                return;
            }
            console.error('사용자 목록 로드 실패:', err);
        } finally {
            setIsLoading(false);
        }
    }, [navigate]);

    // 권한 확인 및 초기화
    useEffect(() => {
        const init = async () => {
            try {
                const authData = await api.checkAuth();

                if (!authData.authenticated) {
                    navigate('/login');
                    return;
                }

                if (!authData.user?.is_admin) {
                    alert('관리자 권한이 필요합니다.');
                    navigate('/');
                    return;
                }

                setAdminUsername(authData.user.username);
                loadUsers();
            } catch (err) {
                console.error('인증 확인 실패:', err);
                navigate('/login');
            }
        };

        init();
    }, [navigate, loadUsers]);

    // 사용자 승인
    const handleApprove = async (userId) => {
        try {
            const result = await api.approveUser(userId);
            if (result.status === 'success') {
                loadUsers();
            } else {
                alert(result.error || '승인에 실패했습니다.');
            }
        } catch (err) {
            console.error('승인 실패:', err);
            alert('서버와 연결할 수 없습니다.');
        }
    };

    // 승인 취소
    const handleRevoke = async (userId) => {
        if (!confirm('이 사용자의 승인을 취소하시겠습니까?')) return;

        try {
            const result = await api.revokeUser(userId);
            if (result.status === 'success') {
                loadUsers();
            } else {
                alert(result.error || '승인 취소에 실패했습니다.');
            }
        } catch (err) {
            console.error('승인 취소 실패:', err);
            alert('서버와 연결할 수 없습니다.');
        }
    };

    // 관리자 권한 토글
    const handleToggleAdmin = async (userId) => {
        if (!confirm('이 사용자에게 관리자 권한을 부여하시겠습니까?')) return;

        try {
            const result = await api.toggleAdmin(userId);
            if (result.status === 'success') {
                loadUsers();
            } else {
                alert(result.error || '권한 변경에 실패했습니다.');
            }
        } catch (err) {
            console.error('권한 변경 실패:', err);
            alert('서버와 연결할 수 없습니다.');
        }
    };

    // 사용자 삭제
    const handleDelete = async (userId) => {
        if (!confirm('정말로 이 사용자를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.')) return;

        try {
            const result = await api.deleteUser(userId);
            if (result.status === 'success') {
                alert(result.message);
                loadUsers();
            } else {
                alert(result.error || '삭제에 실패했습니다.');
            }
        } catch (err) {
            console.error('삭제 실패:', err);
            alert('서버와 연결할 수 없습니다.');
        }
    };

    // 학습 데이터 초기화
    const handleResetProgress = async (userId, username) => {
        if (!confirm(`'${username}' 사용자의 학습 데이터를 초기화하시겠습니까?\n\n초기화 대상:\n- 레벨/XP\n- 로드맵/커리큘럼\n- 배지\n- 레슨 기록\n- 능력치\n\n⚠️ 계정은 유지되지만 이 작업은 되돌릴 수 없습니다.`)) return;

        try {
            const result = await api.resetUserProgress(userId);
            if (result.status === 'success') {
                alert(result.message);
                loadUsers();
            } else {
                alert(result.error || '초기화에 실패했습니다.');
            }
        } catch (err) {
            console.error('초기화 실패:', err);
            alert('서버와 연결할 수 없습니다.');
        }
    };

    // 이메일 마스킹: syjang0803@gmail.com → sy****03@gm***.com
    const maskEmail = (email) => {
        if (!email) return '-';
        const [local, domain] = email.split('@');
        if (!domain) return email;

        const maskLocal = local.length <= 3
            ? local[0] + '**'
            : local.slice(0, 2) + '*'.repeat(Math.min(local.length - 2, 4)) + local.slice(-1);

        return `${maskLocal}@${domain}`;
    };

    // 메시지 전송 모달 상태
    const [messageModal, setMessageModal] = useState({
        isOpen: false,
        targetUser: null, // specific user object or 'all'
        message: ''
    });

    // 메시지 모달 열기
    const openMessageModal = (user = 'all') => {
        setMessageModal({
            isOpen: true,
            targetUser: user,
            message: ''
        });
    };

    // 메시지 전송 핸들러
    const handleSendMessage = async () => {
        const { targetUser, message } = messageModal;
        if (!message.trim()) {
            alert('메시지를 입력해주세요.');
            return;
        }

        try {
            const userIds = targetUser === 'all' ? 'all' : [targetUser.username];
            const result = await api.adminSendMessage(userIds, message);

            if (result.status === 'success') {
                alert(result.message);
                setMessageModal({ ...messageModal, isOpen: false, message: '' });
            } else {
                alert(result.error || '메시지 전송에 실패했습니다.');
            }
        } catch (err) {
            console.error('메시지 전송 실패:', err);
            alert('서버와 연결할 수 없습니다.');
        }
    };

    // 이메일 발송 모달 상태
    const [emailModal, setEmailModal] = useState({
        isOpen: false,
        targetUser: null,
        subject: '',
        body: '',
        isSending: false,
        result: null, // 'success' | 'error' | null
    });

    const openEmailModal = (user) => {
        setEmailModal({ isOpen: true, targetUser: user, subject: '', body: '', isSending: false, result: null });
    };

    const handleSendEmail = async () => {
        const { targetUser, subject, body } = emailModal;
        if (!subject.trim()) { alert('제목을 입력해주세요.'); return; }
        if (!body.trim()) { alert('내용을 입력해주세요.'); return; }

        setEmailModal(prev => ({ ...prev, isSending: true, result: null }));
        try {
            let result;
            if (targetUser === 'all') {
                result = await api.adminSendEmailAll(subject, body);
            } else {
                if (!targetUser?.email) { alert('이 사용자의 이메일 정보가 없습니다.'); return; }
                result = await api.adminSendEmail(targetUser.email, subject, body);
            }
            if (result.status === 'success') {
                setEmailModal(prev => ({ ...prev, isSending: false, result: result.message || 'success' }));
                setTimeout(() => setEmailModal(prev => ({ ...prev, isOpen: false })), 2500);
            } else {
                setEmailModal(prev => ({ ...prev, isSending: false, result: 'error' }));
            }
        } catch (err) {
            console.error('이메일 발송 실패:', err);
            setEmailModal(prev => ({ ...prev, isSending: false, result: 'error' }));
        }
    };

    // 로그아웃
    const handleLogout = async () => {
        try {
            await api.logout();
            navigate('/login');
        } catch (err) {
            console.error('로그아웃 실패:', err);
            navigate('/login');
        }
    };

    const messageButton = (
        <button
            onClick={() => openMessageModal('all')}
            className="flex items-center justify-center w-9 h-9 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white text-xs transition-all duration-300 shadow-[0_0_15px_rgba(79,70,229,0.4)] hover:shadow-[0_0_20px_rgba(79,70,229,0.6)] active:scale-95 border border-transparent"
            title="전체 앱 메시지"
        >
            <i className="fas fa-paper-plane text-sm"></i>
        </button>
    );

    const broadcastEmailButton = (
        <button
            onClick={() => openEmailModal('all')}
            className="flex items-center justify-center w-9 h-9 rounded-full bg-cyan-700 hover:bg-cyan-600 text-white text-xs transition-all duration-300 shadow-[0_0_15px_rgba(8,145,178,0.4)] hover:shadow-[0_0_20px_rgba(8,145,178,0.6)] active:scale-95 border border-transparent"
            title="전체 이메일 발송"
        >
            <i className="fas fa-envelope text-sm"></i>
        </button>
    );

    return (
        <div className="min-h-screen bg-[#0B0E14] text-slate-200 flex flex-col font-sans">
            {/* Background - Grid + Aurora Blobs */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
                <div className="aurora-blob blob-1"></div>
                <div className="aurora-blob blob-2"></div>
                <div className="aurora-blob blob-3"></div>
                <div className="bg-grid-pattern"></div>
            </div>

            {/* 헤더 */}
            <Header
                adminMode={true}
                customAction={<div className="flex items-center gap-2">{messageButton}{broadcastEmailButton}</div>}
                onNavigate={navigate}
                onLogout={handleLogout}
            />

            {/* 메인 컨텐츠 */}
            <main className="flex-grow p-6 relative z-10">
                <div className="max-w-6xl mx-auto">

                    {/* 통계 카드 */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="glass-panel rounded-2xl p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                                    <i className="fas fa-users text-blue-400"></i>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-white font-mono">{stats.total}</p>
                                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">전체 사용자</p>
                                </div>
                            </div>
                        </div>
                        <div className="glass-panel rounded-2xl p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                                    <i className="fas fa-user-check text-green-400"></i>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-white font-mono">{stats.approved}</p>
                                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">승인된 사용자</p>
                                </div>
                            </div>
                        </div>
                        <div className="glass-panel rounded-2xl p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                                    <i className="fas fa-user-clock text-yellow-400"></i>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-white font-mono">{stats.pending}</p>
                                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">승인 대기</p>
                                </div>
                            </div>
                        </div>
                        <div className="glass-panel rounded-2xl p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                                    <i className="fas fa-user-shield text-purple-400"></i>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-white font-mono">{stats.admins}</p>
                                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">관리자</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 사용자 테이블 */}
                    <div className="glass-panel rounded-2xl overflow-hidden">
                        <div className="p-5 border-b border-white/5 flex items-center justify-between">
                            <div>
                                <h2 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">사용자 목록</h2>
                                <p className="text-sm text-white mt-1">계정 및 권한 관리</p>
                            </div>
                            <button
                                onClick={loadUsers}
                                className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                            >
                                <i className="fas fa-sync-alt mr-1"></i>새로고침
                            </button>
                        </div>

                        {isLoading ? (
                            <div className="p-8 text-center text-slate-500">
                                <i className="fas fa-spinner fa-spin text-2xl mb-2"></i>
                                <p>사용자 목록을 불러오는 중...</p>
                            </div>
                        ) : users.length === 0 ? (
                            <div className="p-8 text-center text-slate-500">
                                <i className="fas fa-users-slash text-4xl mb-4"></i>
                                <p>등록된 사용자가 없습니다.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-white/[0.02] text-[10px] text-slate-500 uppercase tracking-wider">
                                        <tr>
                                            <th className="px-3 py-3 text-left">No.</th>
                                            <th className="px-3 py-3 text-left">ID</th>
                                            <th className="px-3 py-3 text-left">닉네임</th>
                                            <th className="px-3 py-3 text-left">이메일</th>
                                            <th className="px-3 py-3 text-center">상태</th>
                                            <th className="px-3 py-3 text-center">권한</th>
                                            <th className="px-3 py-3 text-right">API</th>
                                            <th className="px-3 py-3 text-left">가입일</th>
                                            <th className="px-3 py-3 text-left">최근 로그인</th>
                                            <th className="px-3 py-3 text-center">작업</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                        {users.map((user) => (
                                            <motion.tr
                                                key={user.id}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="hover:bg-white/[0.02] transition-colors"
                                            >
                                                <td className="px-3 py-3 text-[10px] text-slate-600 font-mono">{user.id}</td>
                                                <td className="px-3 py-3 text-sm font-medium text-white truncate">{user.username}</td>
                                                <td className="px-3 py-3 text-sm text-slate-300 truncate">{user.nickname || '-'}</td>
                                                <td className="px-3 py-3 text-xs text-cyan-400/80 font-mono whitespace-nowrap">
                                                    {maskEmail(user.email)}
                                                </td>
                                                <td className="px-3 py-3 text-center">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${user.is_approved
                                                        ? 'bg-green-500/10 text-green-400'
                                                        : 'bg-yellow-500/10 text-yellow-400'
                                                        }`}>
                                                        {user.is_approved ? '승인됨' : '대기중'}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3 text-center">
                                                    {user.is_admin ? (
                                                        <span className="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-purple-500/10 text-purple-400">
                                                            <i className="fas fa-crown mr-1"></i>관리자
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-500 text-xs">일반</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-3 text-right text-sm text-slate-400">
                                                    {(user.api_usage_count || 0).toLocaleString()}
                                                </td>
                                                <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">
                                                    {formatDate(user.created_at)}
                                                </td>
                                                <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">
                                                    {formatDate(user.last_login)}
                                                </td>
                                                <td className="px-3 py-3 text-center">
                                                    <div className="flex items-center justify-center gap-1 flex-nowrap">
                                                        {user.is_approved && (
                                                            <button
                                                                onClick={() => openMessageModal(user)}
                                                                className="px-2 py-1 rounded text-xs text-white bg-indigo-600/80 hover:bg-indigo-500"
                                                                title="앱 메시지 전송"
                                                            >
                                                                <i className="fas fa-comment-dots"></i>
                                                            </button>
                                                        )}
                                                        {user.email && (
                                                            <button
                                                                onClick={() => openEmailModal(user)}
                                                                className="px-2 py-1 rounded text-xs text-white bg-cyan-700/80 hover:bg-cyan-600"
                                                                title="이메일 발송"
                                                            >
                                                                <i className="fas fa-envelope"></i>
                                                            </button>
                                                        )}
                                                        {!user.is_approved && (
                                                            <button
                                                                onClick={() => handleApprove(user.id)}
                                                                className="px-2 py-1 rounded text-xs text-white bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400"
                                                            >
                                                                <i className="fas fa-check"></i>
                                                            </button>
                                                        )}
                                                        {user.is_approved && !user.is_admin && (
                                                            <button
                                                                onClick={() => handleRevoke(user.id)}
                                                                className="px-2 py-1 rounded text-xs text-white bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400"
                                                            >
                                                                <i className="fas fa-times"></i>
                                                            </button>
                                                        )}
                                                        {!user.is_admin && (
                                                            <>
                                                                <button
                                                                    onClick={() => handleToggleAdmin(user.id)}
                                                                    className="px-2 py-1 rounded text-xs text-white bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400"
                                                                >
                                                                    <i className="fas fa-crown"></i>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDelete(user.id)}
                                                                    className="px-2 py-1 rounded text-xs text-white bg-gradient-to-r from-slate-600 to-slate-500 hover:from-red-600 hover:to-red-500"
                                                                    title="사용자 삭제"
                                                                >
                                                                    <i className="fas fa-trash"></i>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleResetProgress(user.id, user.username)}
                                                                    className="px-2 py-1 rounded text-xs text-white bg-gradient-to-r from-orange-600/80 to-amber-500/80 hover:from-orange-500 hover:to-amber-400"
                                                                    title="학습 데이터 초기화"
                                                                >
                                                                    <i className="fas fa-undo"></i>
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* 메시지 전송 모달 */}
            {messageModal.isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="glass-panel rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
                    >
                        <div className="px-6 py-4 border-b border-white/5 flex justify-between items-center">
                            <div>
                                <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">메시지 보내기</h3>
                                <p className="text-sm text-white mt-1">
                                {messageModal.targetUser === 'all' ? '전체 사용자' : `${messageModal.targetUser?.nickname || messageModal.targetUser?.username || ''}`}에게 전송
                                </p>
                            </div>
                            <button
                                onClick={() => setMessageModal({ ...messageModal, isOpen: false })}
                                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="mb-4">
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">받는 사람</label>
                                <div className="p-3 glass-panel rounded-xl text-slate-200 text-sm">
                                    {messageModal.targetUser === 'all' ? (
                                        <span className="text-indigo-400 font-bold"><i className="fas fa-users mr-1"></i>전체 사용자</span>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            <i className="fas fa-user text-cyan-400"></i>
                                            {messageModal.targetUser.nickname || messageModal.targetUser.username} ({messageModal.targetUser.username})
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="mb-6">
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">메시지 내용</label>
                                <textarea
                                    className="w-full h-32 p-3 glass-panel rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 transition-all resize-none"
                                    placeholder="전송할 메시지를 입력하세요..."
                                    value={messageModal.message}
                                    onChange={(e) => setMessageModal({ ...messageModal, message: e.target.value })}
                                    autoFocus
                                ></textarea>
                            </div>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setMessageModal({ ...messageModal, isOpen: false })}
                                    className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 transition-colors text-sm font-medium"
                                >
                                    취소
                                </button>
                                <button
                                    onClick={handleSendMessage}
                                    className="px-4 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white transition-all text-sm font-bold flex items-center gap-2"
                                >
                                    <i className="fas fa-paper-plane"></i>
                                    전송하기
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* 이메일 발송 모달 */}
            {emailModal.isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="glass-panel rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
                    >
                        {/* 상단 그라데이션 바 */}
                        <div className="h-1 bg-gradient-to-r from-cyan-500 to-purple-500"></div>

                        <div className="px-6 py-4 border-b border-white/5 flex justify-between items-center">
                            <div>
                                <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">이메일 발송</h3>
                                <p className="text-sm text-white mt-1 flex items-center gap-2">
                                    {emailModal.targetUser === 'all' ? (
                                        <><i className="fas fa-users text-cyan-400 text-xs"></i>
                                        <span>전체 사용자</span>
                                        <span className="text-slate-500 text-xs">(이메일 등록자 전원)</span></>
                                    ) : (
                                        <><i className="fas fa-user text-cyan-400 text-xs"></i>
                                        {emailModal.targetUser?.nickname || emailModal.targetUser?.username}
                                        <span className="text-slate-500 text-xs font-mono">({maskEmail(emailModal.targetUser?.email)})</span></>
                                    )}
                                </p>
                            </div>
                            <button
                                onClick={() => setEmailModal(prev => ({ ...prev, isOpen: false }))}
                                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            {/* 제목 */}
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">제목</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2.5 glass-panel rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 transition-all text-sm"
                                    placeholder="이메일 제목을 입력하세요"
                                    value={emailModal.subject}
                                    onChange={(e) => setEmailModal(prev => ({ ...prev, subject: e.target.value }))}
                                    disabled={emailModal.isSending}
                                    autoFocus
                                />
                            </div>
                            {/* 본문 */}
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">내용</label>
                                <textarea
                                    className="w-full h-36 p-3 glass-panel rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 transition-all resize-none text-sm"
                                    placeholder="전송할 내용을 입력하세요..."
                                    value={emailModal.body}
                                    onChange={(e) => setEmailModal(prev => ({ ...prev, body: e.target.value }))}
                                    disabled={emailModal.isSending}
                                ></textarea>
                            </div>

                            {/* 결과 메시지 */}
                            {emailModal.result && emailModal.result !== 'error' && (
                                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 text-sm">
                                    <i className="fas fa-check-circle"></i>
                                    {emailModal.result === 'success' ? '이메일이 성공적으로 발송되었습니다.' : emailModal.result}
                                </div>
                            )}
                            {emailModal.result === 'error' && (
                                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                                    <i className="fas fa-exclamation-circle"></i>발송에 실패했습니다. SMTP 설정을 확인해주세요.
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-1">
                                <button
                                    onClick={() => setEmailModal(prev => ({ ...prev, isOpen: false }))}
                                    disabled={emailModal.isSending}
                                    className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 transition-colors text-sm font-medium disabled:opacity-50"
                                >
                                    취소
                                </button>
                                <button
                                    onClick={handleSendEmail}
                                    disabled={emailModal.isSending}
                                    className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white transition-all text-sm font-bold flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-cyan-500/20"
                                >
                                    {emailModal.isSending ? (
                                        <><i className="fas fa-spinner fa-spin"></i>발송 중...</>
                                    ) : (
                                        <><i className="fas fa-paper-plane"></i>발송하기</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* 푸터 */}
            <footer className="text-center py-4 text-[10px] text-slate-600 bg-transparent border-t border-white/5 relative z-10">
                © 2025 <strong className="text-slate-500">LUNA</strong> — Logic Unified Neural Advisor. All Rights Reserved.
            </footer>
        </div>
    );
}
